// Phase 84.M.23 — Reusable athlete-facing notification bell.
// Wraps the working `useUserNotifications` hook + `NotificationSheet` drawer
// (originally only mounted on the Personal page) into a single component so
// every athlete-facing page (Home, Trận đấu, Giải đấu, Bảng xếp hạng,
// Cá nhân) gets the same wired-up bell with real data, real badge count,
// real WebSocket push and the right-side drawer.
import { useEffect, useState } from "react";
import { Bell } from "@phosphor-icons/react";
import { useAuth } from "../../../contexts/AuthContext";
import useUserNotifications from "../../../hooks/useUserNotifications";
import NotificationSheet from "../personal/NotificationSheet";

// Phase 84.M.23 — Global event used by AppShell deep-link action
// `open_notifications` (vd notification toast → "Mở thông báo"). Any mounted
// AthleteNotificationButton instance opens its drawer on this event.
export const OPEN_NOTIFICATIONS_EVENT = "pickleague:open-notifications";

export default function AthleteNotificationButton({
  size = "md",
  testId = "athlete-header-bell",
  className = "",
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const {
    items,
    unreadCount,
    markRead,
    markAllRead,
  } = useUserNotifications({ enabled: Boolean(user?.id), userId: user?.id });

  // Listen for global open event (from AppShell action handler etc.)
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_NOTIFICATIONS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_NOTIFICATIONS_EVENT, onOpen);
  }, []);

  // Two visual sizes: md (40×40, default — used by Personal/Giải đấu/Cá nhân)
  // and sm (36×36, used by Bảng xếp hạng / Trận đấu where icon row is compact).
  const dims = size === "sm"
    ? { btn: "w-9 h-9", icon: 16, badge: "min-w-[16px] h-[16px] text-[9.5px] -top-0.5 -right-0.5" }
    : { btn: "w-10 h-10", icon: 18, badge: "min-w-[18px] h-[18px] text-[10px] top-1 right-1" };

  // Phase 84.M.75 — `className` cho phép caller override style (bg/border/hover)
  // để khớp tone của header màu tối (vd DetailHeader sticky bg-black/40).
  const baseStyle = className
    ? className
    : "bg-white/5 border-white/10 hover:bg-white/8";

  if (!user) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`relative ${dims.btn} rounded-full ${baseStyle} border backdrop-blur-md flex items-center justify-center transition active:scale-95`}
        data-testid={testId}
        aria-label="Thông báo"
      >
        <Bell size={dims.icon} weight="bold" className="text-white" />
        {unreadCount > 0 && (
          <span
            className={`absolute ${dims.badge} px-1 rounded-full bg-[#FF8A00] font-black text-white flex items-center justify-center tabular-nums shadow-[0_0_8px_rgba(255,138,0,0.6)]`}
            data-testid={`${testId}-badge`}
          >
            {unreadCount}
          </span>
        )}
      </button>
      <NotificationSheet
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        unreadCount={unreadCount}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
      />
    </>
  );
}
